import { useQuery, useReactiveVar } from "@apollo/client";
import { withErrorBoundary } from "@sentry/react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  AssessmentType,
  GetChatSessionsForAssessmentQueryQuery,
  GetChatSessionsForAssessmentQueryQueryVariables,
} from "types/graphql-schema";

import getChatSessionsForAssessmentQuery from "@apps/assessments/graphql/get-chat-sessions-for-assessment-query";
import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import { currentOrganizationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize } from "@components/button/button-constants";
import ErrorFallback from "@components/error-fallback/error-fallback";
import Loading from "@components/loading/loading";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";

import { assessmentContextSidebarVar } from "../context-sidebar/context-sidebar-context";

const QuestionContextButtonComponent = ({
  questionId,
  assessmentType,
}: {
  questionId: number;
  assessmentType?: AssessmentType | null;
}) => {
  const params = useParams<{ assessmentId?: string }>();
  const assessmentId = params.assessmentId
    ? parseInt(params.assessmentId)
    : null;
  const assessmentContextSidebar = useReactiveVar(assessmentContextSidebarVar);
  const organization = currentOrganizationVar();
  const { saveUiPreference } = useUiPreferenceCache();
  const [isGeneratingContext, setIsGeneratingContext] = useState(false);

  const { data } = useQuery<
    GetChatSessionsForAssessmentQueryQuery,
    GetChatSessionsForAssessmentQueryQueryVariables
  >(getChatSessionsForAssessmentQuery, {
    variables: { assessmentId: assessmentId || 0 },
    skip: !assessmentId,
  });

  const chatSessions = data?.chatSessions
    ? assertEdgesNonNull(data.chatSessions)
    : [];
  const chatSession = chatSessions.find(
    (chatSession) => chatSession.assessmentQuestionId === questionId,
  );
  const hasChatSession = !!chatSession;

  useEffect(() => {
    let st = null;
    if (isGeneratingContext) {
      if (hasChatSession) {
        setIsGeneratingContext(false);
      } else {
        st = setTimeout(() => {
          setIsGeneratingContext(false);
        }, 30000);
      }
    }
    return () => {
      if (st) {
        clearTimeout(st);
      }
    };
  }, [hasChatSession, isGeneratingContext]);

  if (!questionId) {
    return null;
  }
  if (!organization.featureFlags.reviewContextGeneration) {
    return null;
  }
  if (assessmentType === AssessmentType.EngagementSurvey) {
    return null;
  }
  if (!assessmentType) {
    if (import.meta.env.DEV) {
      return (
        <div className="bg-red-500 text-white text-lg font-medium">
          {questionId}) assessmentType: {assessmentType}
        </div>
      );
    }
    return null;
  }

  return (
    <div className="mt-2 hidden lg:flex justify-end">
      <Button
        onClick={() => {
          if (!chatSession) {
            setIsGeneratingContext(true);
          }
          saveUiPreference({ assessmentSidebarExpanded: true });
          setTimeout(() => {
            assessmentContextSidebarVar({
              questionId,
              showSuggestedResponses: true,
            });
          }, 100);
        }}
        size={ButtonSize.medium}
        className={classNames(
          assessmentContextSidebar?.questionId === questionId
            ? "text-blue-link"
            : "",
        )}
      >
        {!chatSession && isGeneratingContext ? (
          <Loading mini size="4" />
        ) : !chatSession ? (
          <TopicflowIcon type="ai" className="h-3 w-3 text-gray-500" />
        ) : null}
        {chatSession?.id
          ? "View Context"
          : isGeneratingContext
            ? "Generating Context..."
            : "Generate Context"}
      </Button>
    </div>
  );
};

const QuestionContextButton = withErrorBoundary(
  QuestionContextButtonComponent,
  {
    fallback: ErrorFallback,
  },
);

export default QuestionContextButton;
